/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions
} from '@tanstack/react-query';
import {authApiClient, api1881Client} from '#lib/api-client';
import {queryKeys} from '#lib/query-client';
import {setTokensToCookies} from '#utils/cookies';
import type {LoginResponse} from '#types/auth';
import {USER_ROLES} from '@/src/utils/constants';
import {GC_TIME, STALE_TIME} from '@/src/constants';

// Hook for member sign up
export function useSignUpMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => authApiClient.signUpMember(data),
    onSuccess: () => {
      // Invalidate user-related queries
      queryClient.invalidateQueries({queryKey: queryKeys.user.all});
    }
  });
}

// Hook for organisation sign up
export function useSignUpOrganisation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => authApiClient.signUpOrganisation(data),
    onSuccess: () => {
      // Invalidate user-related queries
      queryClient.invalidateQueries({queryKey: queryKeys.user.all});
    }
  });
}

// Hook for merchant sign up
export function useSignUpMerchant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => authApiClient.signUpMerchant(data),
    onSuccess: () => {
      // Invalidate user-related queries
      queryClient.invalidateQueries({queryKey: queryKeys.user.all});
    }
  });
}

// Hook for sign in
export function useSignIn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const response = (await authApiClient.login(data)) as LoginResponse;

      // Handle authentication tokens like the original RTK Query version
      const {token, refresh_token: refreshToken} = response.data || {};

      if (token && refreshToken) {
        await setTokensToCookies({token, refreshToken});
      }

      return response;
    },
    onSuccess: () => {
      // Invalidate user-related queries
      queryClient.invalidateQueries({queryKey: queryKeys.user.all});
    }
  });
}

// Hook for password reset request
export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (data: any) => authApiClient.resetPasswordRequest(data)
  });
}

// Hook for password reset confirm
export function useConfirmPasswordReset() {
  return useMutation({
    mutationFn: (data: any) => authApiClient.resetPassword(data)
  });
}

// Hook for recovery phone change request
export function useRequestRecoveryPhoneChange() {
  return useMutation({
    mutationFn: (data: any) => authApiClient.changeRecoveryPhoneRequest(data)
  });
}

// Hook for recovery phone change confirm
export function useConfirmRecoveryPhoneChange() {
  return useMutation({
    mutationFn: (data: any) => authApiClient.changeRecoveryPhone(data)
  });
}

// Hook for recovery email change confirm
export function useConfirmRecoveryEmailChange() {
  return useMutation({
    mutationFn: (data: any) => authApiClient.changeRecoveryEmail(data)
  });
}

// Hook for phone verification
export function useVerifyPhone() {
  return useMutation({
    mutationFn: (data: any) => {
      // Transform the data to match API expectations
      const transformedData = {
        phone: data.phone,
        entityType: data.role // Transform 'role' to 'entityType'
      };
      return authApiClient.sendOtp(transformedData);
    }
  });
}

// Hook for phone verification for organisations and merchants
export function useResendOtp() {
  return useMutation({
    mutationFn: (data: any) => {
      return authApiClient.resendOtp(data);
    }
  });
}

// Hook for phone verification by phonecall
export function useSendPhoneOtp() {
  return useMutation({
    mutationFn: (data: any) => {
      return authApiClient.resendPhoneOtp(data);
    }
  });
}

// Hook for phone OTP verification
export function useVerifyPhoneOTP() {
  return useMutation({
    mutationFn: (data: any) => {
      // Transform the data to match API expectations
      const transformedData = {
        phone: data.phone,
        code: data.otp,
        entityType: data.role,
        entityId: data.id
      };
      return authApiClient.verifyOtp(transformedData);
    }
  });
}

// Hook for recovery email change
export function useSendRecoveryEmailOtp() {
  return useMutation({
    mutationFn: (data: any) => {
      return authApiClient.sendRecoveryEmailOtp(data);
    }
  });
}

// Hook for recovery email change
export function useResendRecoveryEmailOtp() {
  return useMutation({
    mutationFn: (data: any) => {
      return authApiClient.resendRecoveryEmailOtp(data);
    }
  });
}

// Hook for recovery email change
export function useVerifyRecoveryEmailOtp() {
  return useMutation({
    mutationFn: (data: any) => {
      return authApiClient.verifyRecoveryEmailOtp(data);
    }
  });
}

// Hook for organisation code verification
export function useVerifyOrgCode() {
  return useMutation({
    mutationFn: (data: any) => authApiClient.verifyOrgCode(data.id)
  });
}

// Hook for business number verification (returns status boolean)
export function useCheckBusinessNumber() {
  return useMutation({
    mutationFn: (data: any) => authApiClient.checkBusinessNumber(data.id)
  });
}

// Hook for business number verification (returns status boolean)
export function useCheckMerchantId() {
  return useMutation({
    mutationFn: (data: any) => authApiClient.checkMerchantId(data.id)
  });
}

// Hook for Google code verification
export function useVerifyGoogleCode() {
  return useMutation({
    mutationFn: (data: any) => authApiClient.verifyGoogle(data)
  });
}

// Hook for social prefill
export function useSocialPrefill() {
  return useMutation({
    mutationFn: (data: any) => authApiClient.socialPrefill(data)
  });
}

// Hook for social login
export function useLoginSocial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      const response = (await authApiClient.loginSocial(data)) as LoginResponse;

      // Handle authentication tokens like the original RTK Query version
      const {token, refresh_token: refreshToken} = response.data || {};
      if (token && refreshToken) {
        await setTokensToCookies({token, refreshToken});
      }

      return response;
    },
    onSuccess: () => {
      // Invalidate user-related queries
      queryClient.invalidateQueries({queryKey: queryKeys.user.all});

      // The tokens are now set in cookies, so the user is authenticated
    }
  });
}

// Hook for changing password
export function useNewPassword() {
  return useMutation({
    mutationFn: (data: Record<string, any>) => authApiClient.newPassword(data)
  });
}

// Hook for deleting member account
export function useDeleteMemberAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authApiClient.deleteMemberAccount(),
    onSuccess: () => {
      // Invalidate all queries since user is deleted
      queryClient.clear();
    }
  });
}

// Hook for deleting organisation account
export function useDeleteOrganisationAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authApiClient.deleteOrganisationAccount(),
    onSuccess: () => {
      // Invalidate all queries since organisation is deleted
      queryClient.clear();
    }
  });
}

// Hook for updating contact person
export function useUpdateContactPerson() {
  return useMutation({
    mutationFn: (data: any) => authApiClient.updateContactPerson(data)
  });
}

// Hook for updating business info during signup
export function useUpdateBusinessInfo() {
  return useMutation({
    mutationFn: data => authApiClient.updateBusinessInfo(data)
  });
}

// Hook for updating business info logo during signup
export function useUpdateBusinessInfoLogo() {
  return useMutation({
    mutationFn: ({
      data,
      params
    }: {
      data: FormData;
      params: {entityId: number; entityType: USER_ROLES};
    }) => authApiClient.updateBusinessInfoLogo(params, data)
  });
}

// Hook for updating B2C category during signup
export function useUpdateB2CCategory() {
  return useMutation({
    mutationFn: ({
      id,
      data
    }: {
      id: number | string;
      data: Record<string, any>;
    }) => authApiClient.updateB2CCategory(id, data)
  });
}

// Hook for deleting merchant account
export function useDeleteMerchantAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => authApiClient.deleteMerchantAccount(),
    onSuccess: () => {
      // Invalidate all queries since merchant is deleted
      queryClient.clear();
    }
  });
}

// Hook for searching organisation by number using 1881 API
export function useSearchOrganisationByNumber() {
  return useMutation({
    mutationFn: (data: {organisationNumber: string}) =>
      api1881Client.searchOrganisationByNumber(data)
  });
}

// Hook for searching by phone using 1881 API
export function useSearchByPhone() {
  return useMutation({
    mutationFn: (data: {phone: string}) => api1881Client.searchByPhone(data)
  });
}

// Hook for checking restriction status
export function useCheckRestrictionStatus(
  options?: Omit<UseQueryOptions<any>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...queryKeys.auth.profile(), 'restriction'],
    queryFn: () => authApiClient.checkRestrictionStatus(),
    staleTime: STALE_TIME.USER,
    gcTime: GC_TIME.USER,
    ...options
  });
}
